import { cn } from '@commenda/utils';
import { cva } from 'class-variance-authority';
import React from 'react';
import { Button, ButtonProps } from '../Button';
import { Icons } from '../Icons';

export const alertVariants = cva('flex items-start gap-3 p-4 rounded-md', {
  variants: {
    variant: {
      success: 'bg-bg-success border border-utility-success-100',
      info: 'bg-utility-blue-50 border border-utility-blue-100',
      warning: 'bg-bg-warning !border border-utility-warning-100',
      destructive: 'bg-bg-error border border-utility-error-100',
      default: 'bg-bg-gray border border-border-primary',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export const iconVariants = cva('w-5 h-5', {
  variants: {
    variant: {
      success: 'text-text-success',
      info: 'text-utility-blue-500',
      warning: 'text-text-warning',
      destructive: 'text-text-error',
      default: 'text-utility-gray-500',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface AlertProps {
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  variant?: 'success' | 'info' | 'warning' | 'destructive' | 'default';
  onClose?: () => void;
  _className?: string;
  descriptionClassName?: string;
  action?: {
    label: string;
    onClick: () => void;
    className?: string;
    variant?: ButtonProps['variant'];
    icon?: React.ReactNode;
    size?: ButtonProps['size'];
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    className?: string;
    variant?: ButtonProps['variant'];
  };
  icon?: React.ReactNode;
  classNames?: {
    title?: string;
  };
}

const Alert = ({
  title,
  description,
  variant = 'default',
  onClose,
  action,
  secondaryAction,
  icon,
  _className = '',
  descriptionClassName = '',
  classNames,
}: AlertProps) => {
  return (
    <div className={cn(alertVariants({ variant }), _className)}>
      {icon || <Icons.InfoCircle className={iconVariants({ variant })} />}

      <div className="flex-1 flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          {title && (
            <h3
              className={cn(
                'txt-small-bold text-text-primary',
                classNames?.title || ''
              )}
            >
              {title}
            </h3>
          )}
          {description &&
            (React.isValidElement(description) ? (
              description
            ) : (
              <p
                className={cn(
                  'txt-small-regular text-text-secondary',
                  descriptionClassName
                )}
              >
                {description}
              </p>
            ))}
        </div>

        {action || secondaryAction ? (
          <div className="flex items-center gap-2">
            {action && action.label && (
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  variant={action.variant || 'tertiary'}
                  size={action.size || 'sm'}
                  onClick={action.onClick}
                  className={action.className}
                >
                  {action.icon && action.icon}
                  <span>{action.label}</span>
                  {!action.icon && (
                    <Icons.ArrowNarrowRight className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}

            {secondaryAction && secondaryAction.label && (
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  variant={secondaryAction.variant || 'tertiary'}
                  size="sm"
                  onClick={secondaryAction.onClick}
                  className={secondaryAction.className}
                >
                  <span>{secondaryAction.label}</span>
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {onClose && (
        <button
          onClick={onClose}
          className="size-5 text-fg-quinary cursor-pointer hover:text-fg-quinary-hover"
          type="button"
        >
          <Icons.XClose className="size-5" />
        </button>
      )}
    </div>
  );
};

export { Alert };
